import { useState } from 'react';
import { Link } from 'react-router';
import { motion, AnimatePresence } from 'motion/react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, CheckCircle2, Sparkles, HelpCircle, User, FileText, ChevronDown, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { SEO } from './SEO';
import { StructuredData, getBreadcrumbSchema } from './StructuredData';

const WHATSAPP_NUMBER = '27785870868';
const BOOKING_EMAIL = 'rushncube@support.skivatours.com';
const PHYSICAL_ADDRESS = '5482 Sene St. Protea Glen, Ext 20, Johannesburg, South Africa';

const faqs = [
  {
    q: 'How far in advance should I book my transport?',
    a: 'We recommend booking at least 24 to 48 hours in advance to secure your preferred vehicle, especially for luxury MPVs and specialized safari trips. However, we do accommodate urgent and same-day corporate dispatch depending on fleet availability.'
  },
  {
    q: 'What areas and provinces do you operate in?',
    a: 'While our headquarters and main dispatch operations are based in Johannesburg (Gauteng), Skiva Africa Tours operates country-wide across South Africa and offers specialized cross-border transport services.'
  },
  {
    q: 'Are your vehicles insured and compliant?',
    a: 'Absolutely. Every vehicle in our fleet carries comprehensive passenger liability cover of R500,000. All vehicles undergo strict preventative maintenance schedules and real-time satellite tracking for your absolute peace of mind.'
  },
  {
    q: 'Do you offer custom corporate billing and accounts?',
    a: 'Yes, we partner with numerous organizations for regular corporate drop-offs, executive shuttles, and VIP client transfers. Contact our management team via email to set up a tailored corporate account.'
  }
];

export function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'General Inquiry',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const handleWhatsAppClick = () => {
    const text = `🚖 *SKIVA AFRICA TOURS — Contact Inquiry*\n\nHello Skiva Africa Tours, I would like to make an inquiry regarding your premium passenger transport services.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleEmailClick = () => {
    const subject = `Skiva Africa Tours — General Inquiry`;
    const body = `Hello Skiva Africa Tours,\n\nI would like to inquire about your premium transport services.\n\nBest regards,`;
    window.location.href = `mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleMapClick = () => {
    window.open(`https://maps.google.com/?q=${encodeURIComponent(PHYSICAL_ADDRESS)}`, '_blank');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate sending inquiry
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1200);
  };

  return (
    <div className="flex flex-col min-h-screen bg-primary overflow-hidden">
      <SEO 
        title="Contact Skiva Africa Tours | Johannesburg Transport Services"
        description="Contact Skiva Africa Tours for professional airport transfers, private transport, corporate transportation, shuttle services, group travel and tours in Johannesburg and across South Africa."
        canonical="https://skivatours.com/contact"
      />
      <StructuredData 
        data={getBreadcrumbSchema([
          { name: 'Home', url: 'https://skivatours.com/' },
          { name: 'Contact', url: 'https://skivatours.com/contact' }
        ])} 
      />
      {/* ─── HERO HEADER ─── */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-gradient-to-b from-black via-primary to-primary">
        {/* Background glow & texture */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-gold-base/10 via-transparent to-transparent opacity-60 pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.02] bg-[radial-gradient(#d4af37_1px,transparent_1px)] [background-size:32px_32px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className="inline-flex items-center gap-2 bg-gold-base/10 border border-gold-base/20 px-4 py-2 rounded-full mb-6 backdrop-blur-md">
              <Sparkles className="w-4 h-4 text-gold-base" />
              <span className="text-gold-base text-xs font-bold tracking-[0.25em] uppercase">Connect With Us</span>
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white uppercase tracking-tighter leading-tight mb-6">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Touch</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-white/70 max-w-2xl mx-auto font-light leading-relaxed tracking-wide">
              Experience executive hospitality from the moment you reach out. We are here to assist with your premium passenger transport and logistics requirements. Ready to reserve? You can also <Link to="/book" className="text-gold-base hover:underline font-medium">book a private transfer</Link> directly online.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── CONTACT INFO CARDS & FORM SECTION ─── */}
      <section className="relative py-12 md:py-20 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* LEFT COLUMN: CONTACT INFO CARDS (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <p className="text-gold-base text-xs font-bold tracking-[0.3em] uppercase mb-2">Direct Channels</p>
                <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-6">Contact Information</h2>
              </div>

              {/* Card 1: Phone & Contact Persons */}
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="p-6 bg-[#141414] border border-white/5 hover:border-gold-base/30 rounded-sm transition-all duration-500 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gold-base/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base flex-shrink-0 group-hover:bg-gold-base/20 transition-colors">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-1">Call / WhatsApp</p>
                    <p className="text-base font-bold text-white mb-2 tracking-wide">Phone: (011) 078 587 0868</p>
                    <div className="text-sm font-light text-white/80 space-y-1 mb-4 border-t border-white/5 pt-2">
                      <p><strong className="text-white font-medium">Mlungisi Ncube:</strong> 078 587 0868</p>
                      <p><strong className="text-white font-medium">Ntombifuthi Ncube:</strong> 083 212 9263</p>
                    </div>
                    <button
                      onClick={handleWhatsAppClick}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white text-xs font-bold uppercase tracking-wider rounded-sm shadow-lg hover:bg-[#20BA5C] transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat on WhatsApp
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Card 2: Email */}
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="p-6 bg-[#141414] border border-white/5 hover:border-gold-base/30 rounded-sm transition-all duration-500 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gold-base/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base flex-shrink-0 group-hover:bg-gold-base/20 transition-colors">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-1">Email Inquiries</p>
                    <div className="text-sm font-bold text-white mb-4 space-y-1 break-all">
                      <p className="text-gold-base">rushncube@support.skivatours.com</p>
                      <p className="text-white/70 font-light text-xs">paulinencube@support.skivatours.com</p>
                    </div>
                    <button
                      onClick={handleEmailClick}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-white/10 text-white text-xs font-bold uppercase tracking-wider rounded-sm shadow-lg hover:bg-[#252525] hover:border-gold-base/30 transition-all"
                    >
                      <Mail className="w-4 h-4 text-gold-base" />
                      Send an Email
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Card 3: Physical Address */}
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="p-6 bg-[#141414] border border-white/5 hover:border-gold-base/30 rounded-sm transition-all duration-500 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gold-base/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base flex-shrink-0 group-hover:bg-gold-base/20 transition-colors">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-1">Headquarters</p>
                    <p className="text-sm font-light text-white/90 mb-4 leading-relaxed">
                      5482 Sene St. Protea Glen, Ext 20<br />
                      Johannesburg, South Africa
                    </p>
                    <button
                      onClick={handleMapClick}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-white/10 text-white text-xs font-bold uppercase tracking-wider rounded-sm shadow-lg hover:bg-[#252525] hover:border-gold-base/30 transition-all"
                    >
                      <MapPin className="w-4 h-4 text-gold-base" />
                      View on Google Maps
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Card 4: Operating Hours */}
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="p-6 bg-gradient-to-br from-[#1a1a1a] to-[#141414] border border-gold-base/20 rounded-sm shadow-xl relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gold-base/5 rounded-bl-full pointer-events-none" />
                <div className="relative z-10 flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-gold-base/20 flex items-center justify-center text-gold-base flex-shrink-0">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-gold-base text-xs font-bold uppercase tracking-widest mb-1">Operating Hours</p>
                    <p className="text-base font-bold text-white mb-1">24/7 Dispatch & Support</p>
                    <p className="text-white/60 text-xs font-light leading-relaxed">
                      Our operational team and mechanics are on 24-hour standby for premium transfers, corporate logistics, and emergency dispatch.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* RIGHT COLUMN: INTERACTIVE CONTACT FORM (7 cols) */}
            <div className="lg:col-span-7">
              <div className="p-8 sm:p-10 md:p-12 bg-[#141414] border border-white/5 rounded-sm shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-light via-gold-base to-gold-base/50" />

                <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Send an Inquiry</h3>
                <p className="text-white/60 text-sm font-light mb-8">
                  Fill out the form below and our executive booking team will reach out to you within 2 business hours.
                </p>

                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="py-12 text-center space-y-6"
                    >
                      <div className="w-20 h-20 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center mx-auto text-emerald-500 shadow-lg shadow-emerald-500/10">
                        <CheckCircle2 className="w-10 h-10" />
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-2xl font-bold text-white uppercase tracking-wider">Inquiry Received</h4>
                        <p className="text-white/70 max-w-md mx-auto text-sm font-light leading-relaxed">
                          Thank you for contacting Skiva Africa Tours. We have received your message and our dispatch team is reviewing your requirements.
                        </p>
                      </div>
                      <button
                        onClick={() => { setSubmitted(false); setFormState({ name: '', email: '', phone: '', service: 'General Inquiry', message: '' }); }}
                        className="px-8 py-3 bg-gold-base text-black font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-gold-light transition-colors shadow-lg"
                      >
                        Send Another Message
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={handleSubmit}
                      className="space-y-6"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Name */}
                        <div className="space-y-2">
                          <label htmlFor="name" className="block text-xs font-bold text-white/70 uppercase tracking-widest">
                            Full Name <span className="text-gold-base">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
                              <User className="w-4 h-4" />
                            </div>
                            <input
                              id="name"
                              type="text"
                              required
                              value={formState.name}
                              onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                              placeholder="e.g. Nelson Khumalo"
                              className="w-full bg-[#1a1a1a] border border-white/10 rounded-sm py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-base focus:ring-1 focus:ring-gold-base transition-all"
                            />
                          </div>
                        </div>

                        {/* Phone */}
                        <div className="space-y-2">
                          <label htmlFor="phone" className="block text-xs font-bold text-white/70 uppercase tracking-widest">
                            Phone Number <span className="text-gold-base">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
                              <Phone className="w-4 h-4" />
                            </div>
                            <input
                              id="phone"
                              type="tel"
                              required
                              value={formState.phone}
                              onChange={(e) => setFormState({ ...formState, phone: e.target.value })}
                              placeholder="e.g. +27 82 123 4567"
                              className="w-full bg-[#1a1a1a] border border-white/10 rounded-sm py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-base focus:ring-1 focus:ring-gold-base transition-all"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <label htmlFor="email" className="block text-xs font-bold text-white/70 uppercase tracking-widest">
                          Email Address <span className="text-gold-base">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
                            <Mail className="w-4 h-4" />
                          </div>
                          <input
                            id="email"
                            type="email"
                            required
                            value={formState.email}
                            onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                            placeholder="e.g. nelson@company.co.za"
                            className="w-full bg-[#1a1a1a] border border-white/10 rounded-sm py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-base focus:ring-1 focus:ring-gold-base transition-all"
                          />
                        </div>
                      </div>

                      {/* Service Select */}
                      <div className="space-y-2">
                        <label htmlFor="service" className="block text-xs font-bold text-white/70 uppercase tracking-widest">
                          Service of Interest
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none">
                            <FileText className="w-4 h-4" />
                          </div>
                          <select
                            id="service"
                            value={formState.service}
                            onChange={(e) => setFormState({ ...formState, service: e.target.value })}
                            className="w-full bg-[#1a1a1a] border border-white/10 rounded-sm py-3.5 pl-11 pr-10 text-sm text-white focus:outline-none focus:border-gold-base focus:ring-1 focus:ring-gold-base transition-all appearance-none cursor-pointer"
                          >
                            <option value="General Inquiry">General Inquiry</option>
                            <option value="Airport Transfer">Airport Transfer</option>
                            <option value="Corporate Drop-off">Corporate Drop-off</option>
                            <option value="Shuttle Service">Shuttle Service</option>
                            <option value="Safari & Game Drive">Safari & Game Drive</option>
                            <option value="Cross-border Transport">Cross-border Transport</option>
                          </select>
                          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                        </div>
                      </div>

                      {/* Message */}
                      <div className="space-y-2">
                        <label htmlFor="message" className="block text-xs font-bold text-white/70 uppercase tracking-widest">
                          Your Message <span className="text-gold-base">*</span>
                        </label>
                        <textarea
                          id="message"
                          required
                          rows={4}
                          value={formState.message}
                          onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                          placeholder="Please describe your transport requirements, date, or itinerary details..."
                          className="w-full bg-[#1a1a1a] border border-white/10 rounded-sm p-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-base focus:ring-1 focus:ring-gold-base transition-all resize-none"
                        />
                      </div>

                      {/* Submit Button */}
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={submitting}
                        className="w-full py-4 bg-gold-base text-black font-extrabold text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold-light transition-all flex items-center justify-center gap-3 shadow-lg shadow-gold-base/20 disabled:opacity-50"
                      >
                        {submitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                            <span>Transmitting...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            <span>Send Message</span>
                          </>
                        )}
                      </motion.button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── FAQ SECTION ─── */}
      <section className="relative py-20 md:py-28 bg-[#0a0a0a] border-t border-white/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gold-base/10 px-4 py-2 rounded-full mb-4">
              <HelpCircle className="w-4 h-4 text-gold-base" />
              <span className="text-gold-base text-xs font-bold tracking-[0.2em] uppercase">Common Questions</span>
            </div>
            <h2 className="text-3xl font-black text-white uppercase tracking-tight">Frequently Asked Questions</h2>
            <p className="text-white/60 text-sm font-light mt-4 max-w-xl mx-auto">
              Everything you need to know about booking, fleet operations, and corporate accounts with Skiva Africa Tours.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = activeFaq === index;
              return (
                <motion.div
                  key={index}
                  className="bg-[#141414] border border-white/5 rounded-sm overflow-hidden"
                >
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : index)}
                    className="w-full p-6 text-left flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <span className="font-bold text-white text-base sm:text-lg pr-4">{faq.q}</span>
                    <div className={cn("w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 transition-transform duration-300", isOpen && "rotate-180 bg-gold-base text-black")}>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="p-6 pt-0 text-white/60 font-light text-sm sm:text-base leading-relaxed border-t border-white/5 bg-black/20">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <p className="text-white/60 text-sm font-light mb-3">Ready to schedule your transportation?</p>
            <Link 
              to="/book" 
              className="inline-flex items-center text-gold-base font-bold text-xs uppercase tracking-widest hover:text-white transition-colors"
            >
              Book a Private Transfer Online <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
